// CSV-eksport til regnskabsbrug.
//
// Filerne skal kunne åbnes direkte i Excel og bruges til afstemning, og det stiller tre krav
// som er nemme at overse:
//
//   1) UTF-8 BOM forrest. Uden den læser Excel filen som Windows-1252, og æ/ø/å bliver til
//      mojibake. (Samme byte-sekvens som nginx afviser i en konfigurationsfil — her er den
//      til gengæld påkrævet.)
//   2) Semikolon som feltadskiller når tal bruger komma som decimaltegn. Ellers deler Excel
//        1234,56
//      op i to kolonner. Adskilleren følger derfor talformatet, ikke omvendt.
//   3) Ingen tusindtalsseparator i tal. "1234,56" parses som et tal uanset landeindstilling;
//      "1.234,56" kun med dansk. Uden separatoren kan man regne videre på kolonnen med det samme.
import type { NumberFormat } from '../context/SettingsContext';

/** Konstrueret frem for skrevet: et bogstaveligt BOM-tegn ville være usynligt i editoren
 *  og bliver flagget som "irregular whitespace" af eslint. */
const BOM = String.fromCharCode(0xFEFF);

export interface CsvColumn<T> {
  header: string;
  /** Returnér den færdige celletekst. Tal formateres med csvNumber(). */
  value: (row: T) => string | number | null | undefined;
}

/** Tal til CSV: brugerens decimaltegn, ingen tusindtalsseparator. */
export function csvNumber(value: number | string | null | undefined, fmt: NumberFormat, decimals = 2): string {
  if (value == null || value === '') return '';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (!isFinite(num)) return '';
  const s = num.toFixed(decimals);
  return fmt === 'dk' ? s.replace('.', ',') : s;
}

/** Feltadskiller: semikolon når decimaltegnet er komma, ellers komma. */
export function csvSeparator(fmt: NumberFormat): string {
  return fmt === 'dk' ? ';' : ',';
}

// Et felt skal i anførselstegn hvis det indeholder adskilleren, anførselstegn eller linjeskift.
// Anførselstegn inde i feltet fordobles — det er RFC 4180, og Excel følger den.
function escapeCell(raw: string | number | null | undefined, sep: string): string {
  if (raw == null) return '';
  const s = String(raw);
  if (s.includes(sep) || s.includes('"') || s.includes('\n') || s.includes('\r')) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}

/** Byg CSV-teksten (uden BOM). Eksporteret separat så den kan testes uden en browser. */
export function buildCsv<T>(columns: CsvColumn<T>[], rows: T[], fmt: NumberFormat): string {
  const sep = csvSeparator(fmt);
  const lines = [columns.map(c => escapeCell(c.header, sep)).join(sep)];
  for (const row of rows) {
    lines.push(columns.map(c => escapeCell(c.value(row), sep)).join(sep));
  }
  // CRLF er hvad RFC 4180 foreskriver, og hvad ældre regnearksprogrammer forventer.
  return lines.join('\r\n');
}

/** Byg CSV og start download. Filnavnet får tidsstempel, så gentagne eksporter ikke kolliderer. */
export function downloadCsv<T>(
  baseName: string,
  columns: CsvColumn<T>[],
  rows: T[],
  fmt: NumberFormat
): void {
  const csv = buildCsv(columns, rows, fmt);
  const d = new Date();
  const p2 = (n: number) => String(n).padStart(2, '0');
  const stamp = `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}-${p2(d.getHours())}${p2(d.getMinutes())}`;
  // BOM'en lægges på her og ikke i buildCsv(), så den funktion forbliver ren tekst.
  const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${baseName}-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
